/**
 * FI Console API Service
 *
 * Handles all HTTP requests to the FI Console backend endpoints.
 * Base URL: http://localhost:3019/api/fi-console
 */

import type {
  DashboardResponse,
  GuidelineDetailResponse,
  GuidelineComplianceSummary,
  ComplianceGap,
  ActionItem,
  ComplianceControl,
  SystemMapping,
  SemanticSearchResponse,
} from '../types/fiConsole';

// Use window.location.origin to automatically use the correct protocol and host (works through nginx proxy)
// For production: https://osfi.analyticlabs.io/api/fi-console
// For local dev: http://localhost:3019/api/fi-console (or through nginx on port 80)
const API_BASE_URL = typeof window !== 'undefined' && window.location.hostname !== 'localhost'
  ? `${window.location.origin}/api/fi-console`
  : `http://${window.location.hostname}:3019/api/fi-console`;
const API_KEY = 'rooke_analyst_c63128c3f026feef7f1bdf438b942cfbd9eb462a02a25df325f9afbac6074240';

/**
 * Common headers for all API requests
 */
const getHeaders = (): HeadersInit => ({
  'Content-Type': 'application/json',
  'X-API-Key': API_KEY,
});

/**
 * Generic fetch wrapper with error handling
 */
async function apiFetch<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        ...getHeaders(),
        ...(options?.headers || {}),
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API request failed: ${response.status} ${response.statusText} - ${errorText}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`API Error (${endpoint}):`, error);
    throw error;
  }
}

/**
 * FI Console API Service
 */
export const fiConsoleApi = {
  /**
   * Get dashboard data with summary, guidelines, gaps, and action items
   */
  async getDashboard(): Promise<DashboardResponse> {
    return apiFetch<DashboardResponse>('/dashboard');
  },

  /**
   * Get detailed information for a specific guideline
   */
  async getGuidelineDetail(guidelineId: string): Promise<GuidelineDetailResponse> {
    return apiFetch<GuidelineDetailResponse>(`/guidelines/${guidelineId}`);
  },

  /**
   * Get list of all guidelines with compliance summaries
   */
  async getGuidelines(): Promise<GuidelineComplianceSummary[]> {
    return apiFetch<GuidelineComplianceSummary[]>('/guidelines');
  },

  /**
   * Get all compliance gaps, optionally filtered by guideline
   */
  async getGaps(guidelineId?: string): Promise<ComplianceGap[]> {
    const endpoint = guidelineId ? `/gaps?guideline_id=${guidelineId}` : '/gaps';
    return apiFetch<ComplianceGap[]>(endpoint);
  },

  /**
   * Get all action items, optionally filtered by status or priority
   */
  async getActionItems(status?: string, priority?: string): Promise<ActionItem[]> {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (priority) params.append('priority', priority);

    const endpoint = params.toString() ? `/action-items?${params.toString()}` : '/action-items';
    return apiFetch<ActionItem[]>(endpoint);
  },

  /**
   * Get all controls, optionally filtered by guideline
   */
  async getControls(guidelineId?: string): Promise<ComplianceControl[]> {
    const endpoint = guidelineId ? `/controls?guideline_id=${guidelineId}` : '/controls';
    return apiFetch<ComplianceControl[]>(endpoint);
  },

  /**
   * Get system mappings, optionally filtered by guideline
   */
  async getSystemMappings(guidelineId?: string): Promise<SystemMapping[]> {
    const endpoint = guidelineId ? `/mappings?guideline_id=${guidelineId}` : '/mappings';
    return apiFetch<SystemMapping[]>(endpoint);
  },

  /**
   * Update a gap (assign, change status, etc.)
   */
  async updateGap(
    gapId: string,
    updates: {
      status?: string;
      assigned_to?: string;
      due_date?: string;
      resolution_notes?: string;
    }
  ): Promise<ComplianceGap> {
    return apiFetch<ComplianceGap>(`/gaps/${gapId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  },

  /**
   * Update an action item
   */
  async updateActionItem(
    actionId: string,
    updates: {
      status?: string;
      assigned_to?: string;
      due_date?: string;
    }
  ): Promise<ActionItem> {
    return apiFetch<ActionItem>(`/action-items/${actionId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  },

  /**
   * Run gap detection for a specific guideline or all guidelines
   */
  async runGapDetection(guidelineId?: string, forceRefresh: boolean = false): Promise<any> {
    return apiFetch('/gap-detection/run', {
      method: 'POST',
      body: JSON.stringify({
        guideline_id: guidelineId,
        force_refresh: forceRefresh,
      }),
    });
  },

  /**
   * Calculate compliance score for a guideline
   */
  async calculateCompliance(guidelineId: string, asOfDate?: string): Promise<any> {
    return apiFetch('/compliance/calculate', {
      method: 'POST',
      body: JSON.stringify({
        guideline_id: guidelineId,
        as_of_date: asOfDate,
      }),
    });
  },

  /**
   * Search semantic contracts via Zeus Memory
   */
  async search(query: string, guidelineIds?: string[], topK: number = 10): Promise<SemanticSearchResponse> {
    return apiFetch<SemanticSearchResponse>('/search', {
      method: 'POST',
      body: JSON.stringify({
        query,
        guideline_ids: guidelineIds,
        top_k: topK,
      }),
    });
  },
};

export default fiConsoleApi;
